import { cx } from '@emotion/css';
import {
  Button as ButtonSDS,
  FormattedMessage,
  MotifScheme,
} from '@snapchat/snap-design-system-marketing';
import type { FC, Ref } from 'react';
import { createRef, useContext, useEffect, useState } from 'react';

import { CartContext } from '../Cart/CartProvider';
import { ShopifyContext } from '../ShopifyProvider';
import { addToCartButtonCss, addToCartTooltipCss, disabledButtonCss } from './styles';
import type { AddToCartProps } from './types';

const AddToCartButton: FC<AddToCartProps> = ({ productKey = '', optionId, showTooltip }) => {
  const cart = useContext(CartContext);
  const { catalog, currencyCode = '' } = useContext(ShopifyContext);
  const product = catalog[productKey];

  if (!product) {
    return null;
  }

  // Use provided optionId or fall back to first option
  const resolvedOptionId = optionId || product.options[0]?.id;
  const quantityInCart = cart.lineItems[resolvedOptionId ?? ''] ?? 0;
  const disabled = quantityInCart === product.maxQuantity || !product.available;

  const renderTooltip = () => {
    if (quantityInCart === product.maxQuantity) {
      return (
        <FormattedMessage
          id="maxCartQuantity"
          values={{ quantity: String(product.maxQuantity) }}
          defaultMessage="Limit {quantity} per customer."
        />
      );
    }

    return null;
  };

  return (
    <>
      <ButtonSDS
        type="Primary"
        className={disabled ? disabledButtonCss : undefined}
        onClick={() =>
          cart.addOne({
            productKey,
            optionId: resolvedOptionId ?? '',
            currencyCode,
          })
        }
        disabled={disabled}
      >
        <FormattedMessage id="addToCart" defaultMessage="Add to Cart" />
      </ButtonSDS>
      {showTooltip && disabled && <span className={addToCartTooltipCss}>{renderTooltip()}</span>}
    </>
  );
};

export const AddToCart: FC<AddToCartProps> = ({ className, productKey = '', optionId }) => {
  const { store, catalog } = useContext(ShopifyContext);
  const isProductAvailableInCountry = !!store;
  const buttonRef: Ref<HTMLDivElement> = createRef();
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const product = catalog[productKey];

  useEffect(() => {
    const button = buttonRef.current;

    if (!button) return;

    const onButtonMouseEnter = () => setShowTooltip(true);
    const onButtonMouseLeave = () => setShowTooltip(false);

    button.addEventListener('mouseenter', onButtonMouseEnter);
    button.addEventListener('mouseleave', onButtonMouseLeave);

    return () => {
      button.removeEventListener('mouseenter', onButtonMouseEnter);
      button.removeEventListener('mouseleave', onButtonMouseLeave);
    };
  }, [buttonRef]);

  return (
    <div ref={buttonRef} className={cx(className, MotifScheme.DEFAULT, addToCartButtonCss)}>
      {isProductAvailableInCountry && product?.available ? (
        <AddToCartButton productKey={productKey} optionId={optionId} showTooltip={showTooltip} />
      ) : (
        <>
          {/* TODO: Add this to localized messages when we have a shop again. */}
          Product not available in your country
        </>
      )}
    </div>
  );
};

AddToCart.displayName = 'AddToCart';
